/**
 * Pure batching and acknowledgement logic for the telemetry stream
 * (services/telemetry.js). No I/O, no timers.
 */

/** Periodic batch caps. */
export const PERIODIC_MAX_EVENTS = 50;
export const PERIODIC_MAX_BYTES = 60 * 1024;
/** Final keepalive flush cap: browsers allow 64 KB of in-flight keepalive bodies per page. */
export const KEEPALIVE_MAX_BYTES = 32 * 1024;

const byteLength = (s) =>
  typeof TextEncoder !== "undefined" ? new TextEncoder().encode(s).length : s.length;

/** The core fields of an entry, sent in place of one too large for any batch. */
function stub(entry) {
  const { event, seq, t_mono, captured_at, view } = entry;
  return { event, seq, t_mono, captured_at, view };
}

/**
 * Build one request body from `entries` (ascending seq, all unacknowledged).
 *
 * Every entry is sent as itself: no send-time collapsing. A periodic batch and a
 * keepalive batch can overlap, and the server keeps the first row per seq, so a
 * collapsed row whose count depended on where a batch ended could lose repeats.
 * Entries collapsed at capture (a `repeat_count` on one ring entry) are unaffected.
 * Returns `{ body, fromSeq, toSeq, lossSeqs }` where `fromSeq..toSeq` is the seq range
 * the body covers and `lossSeqs` the seqs a permanent rejection would lose, or null
 * when there is nothing to send.
 */
export function buildBatch(entries, { tabId, maxEvents = Infinity, maxBytes, fromSeq }) {
  if (entries.length === 0) return null;
  const out = [];
  const lossSeqs = [];
  const seqs = [];
  let toSeq = null;
  const overhead = byteLength(JSON.stringify({ tab_id: tabId, events: [] }));
  let bytes = overhead;
  for (const entry of entries) {
    if (out.length >= maxEvents) break;
    let item = { ...entry };
    let size = byteLength(JSON.stringify(item)) + (out.length > 0 ? 1 : 0);
    if (bytes + size > maxBytes) {
      if (out.length > 0) break;
      // A single entry too large for any batch would block the stream forever.
      item = stub(entry);
      size = byteLength(JSON.stringify(item));
    }
    out.push(item);
    seqs.push(entry.seq);
    // A rejected telemetry.dropped report is not a new loss: counting it would
    // generate another report for every rejection.
    if (entry.event !== "telemetry.dropped") lossSeqs.push(entry.seq);
    bytes += size;
    toSeq = entry.seq;
  }
  if (toSeq === null) return null;
  const start = fromSeq ?? entries[0].seq;
  return {
    body: JSON.stringify({ tab_id: tabId, events: out }),
    eventCount: out.length,
    seqs,
    lossCount: lossSeqs.length,
    lossSeqs,
    fromSeq: start,
    toSeq,
  };
}

/** Statuses after which a batch is never resent: the server rejected it outright. */
const PERMANENT_STATUSES = new Set([400, 403, 413]);

/**
 * Classify a send outcome: "ok" (2xx), "permanent" (400 malformed, 403 bad
 * origin, 413 too large — resending cannot succeed) or "retry" (429, 401, 5xx,
 * network error / status 0, anything else).
 */
export function classifyStatus(status) {
  if (status >= 200 && status < 300) return "ok";
  if (PERMANENT_STATUSES.has(status)) return "permanent";
  return "retry";
}

/**
 * Advance `watermark` through `ranges` (acknowledged `[from, to]` seq ranges)
 * while they are contiguous with it. Returns the new watermark and the ranges
 * still waiting on an earlier gap.
 */
export function advanceWatermark(watermark, ranges) {
  let w = watermark;
  let pending = ranges.filter(([, to]) => to > w).sort((a, b) => a[0] - b[0]);
  let moved = true;
  while (moved) {
    moved = false;
    for (const [from, to] of pending) {
      if (from <= w + 1 && to > w) {
        w = to;
        moved = true;
      }
    }
    pending = pending.filter(([, to]) => to > w);
  }
  return { watermark: w, pending };
}

/** `{ from, to }` seq range of buffered entries (a bug report's window), nulls when empty. */
export function seqRange(entries) {
  const seqs = entries.map((e) => e.seq).filter(Number.isInteger);
  if (seqs.length === 0) return { from: null, to: null };
  return { from: Math.min(...seqs), to: Math.max(...seqs) };
}
